import React, { useCallback, useContext, useMemo, useState } from 'react';
import { IconButton, Stack, Text } from '@fluentui/react';
import { ThemeContext } from '../../../HubLayout/models/ThemeContext';
import { getThemeFromString } from '../../../Common/util/localStorageUtils';
import { SectionIcon } from './SectionCard';
import { getKustoClusterForCurrentCloud } from './kustoAccessConfig';

export interface IClusterBannerProps {
    /** Wording above the address, so each page can frame it for its own audience. */
    label?: string;
}

/**
 * Names the cluster the page acts against, with a copy button. Rendered above the section cards
 * on both Kusto pages so the endpoint is never ambiguous.
 */
export const ClusterBanner: React.FC<IClusterBannerProps> = ({
    label = 'Cluster endpoint',
}) => {
    const themeContext = useContext(ThemeContext);
    const theme = getThemeFromString(themeContext.themeName);

    const CLUSTER = useMemo(() => getKustoClusterForCurrentCloud(), []);

    const [copied, setCopied] = useState<boolean>(false);

    const copy = useCallback(async () => {
        try {
            await navigator.clipboard.writeText(CLUSTER);
            setCopied(true);
            setTimeout(() => setCopied(false), 2000);
        } catch {
            // Clipboard access can be denied; the address stays selectable on the page.
        }
    }, [CLUSTER]);

    if (!CLUSTER) {
        return null;
    }

    return (
        <Stack
            horizontal
            verticalAlign="center"
            tokens={{ childrenGap: 14 }}
            styles={{
                root: {
                    backgroundColor: theme.surfaces.container,
                    border: `1px solid ${theme.surfaces.chromeBorder}`,
                    borderRadius: 8,
                    // Horizontal padding and gap match SectionCard so this badge and its text
                    // line up with the cards below.
                    padding: '12px 24px',
                },
            }}
        >
            <SectionIcon icon="Database" />
            <Stack
                tokens={{ childrenGap: 2 }}
                styles={{ root: { flexGrow: 1 } }}
            >
                <Text
                    variant="smallPlus"
                    styles={{
                        root: {
                            fontWeight: 600,
                            color: theme.palette.neutralSecondary,
                        },
                    }}
                >
                    {label}
                </Text>
                <Text
                    variant="mediumPlus"
                    styles={{
                        root: {
                            fontFamily: 'Consolas, Menlo, monospace',
                            wordBreak: 'break-all',
                        },
                    }}
                >
                    {CLUSTER}
                </Text>
            </Stack>
            <IconButton
                iconProps={{ iconName: copied ? 'CheckMark' : 'Copy' }}
                title={copied ? 'Copied' : 'Copy cluster endpoint'}
                ariaLabel={
                    copied ? 'Cluster endpoint copied' : 'Copy cluster endpoint'
                }
                onClick={() => void copy()}
            />
        </Stack>
    );
};
